"use client"

import clsx from "clsx"
import React, { RefObject, useEffect, useState } from "react"
import { useDataAttributes } from "../../hooks"

/**
 * @description 미디어 카드 레이아웃. 헤더, 푸터, 오버레이 영역을 포함하는 카드 컨테이너
 * @example
 * ```tsx
 * <TMediaCard
 *   cardHeader={<span>Title</span>}
 *   cardFooter={<span>Footer</span>}
 *   visibleHoverRing
 *   onClick={handleClick}
 * >
 *   <TMediaImage src="/image.jpg" />
 * </TMediaCard>
 * ```
 */
interface TMediaCardProps {
	/** 카드 DOM ref */
	cardRef?: ((instance: HTMLDivElement | null) => void) | RefObject<HTMLDivElement> | null | undefined
	/** 카드 상단 헤더 영역 */
	cardHeader?: React.ReactNode
	/** 카드 하단 푸터 영역 */
	cardFooter?: React.ReactNode
	/** 미디어 위에 표시되는 오버레이 콘텐츠 */
	cardOverlayContents?: React.ReactNode
	/** 추가 CSS 클래스 */
	className?: string
	/** 카드 전체 선택 상태 */
	cardSelected?: boolean
	/** 미디어 영역 선택 상태 */
	mediaSelected?: boolean
	/**
	 * 호버 시 테두리 강조
	 * @default false
	 */
	visibleHoverRing?: boolean
	/**
	 * 호버 시 어둡게 처리
	 * @default false
	 */
	visibleHoverDim?: boolean
	/**
	 * 드래그 가능 여부
	 * @default false
	 */
	draggable?: boolean
	/**
	 * 메뉴 열림 상태. true 시 호버 상태 해제
	 * @default false
	 */
	isMenuOpen?: boolean
	/** data-* 속성 맵 */
	dataAttributes?: Record<string, string | number | boolean>
	/** ReactNode 또는 (hover: boolean) => ReactNode 형태의 렌더 함수 */
	children: React.ReactNode | ((hover: boolean) => React.ReactNode)
	/** 카드 클릭 핸들러 */
	onClick?: () => void
	/**
	 * 접근 가능한 카드 이름. onClick과 함께 지정하면 클릭 영역이 키보드로 포커스·Enter/Space로 활성화 가능한
	 * button 시맨틱(role="button" + tabIndex)으로 전환된다. 지정하지 않으면 기존과 동일하게 비활성 상태를 유지한다
	 * (이름 없는 button이 스크린리더에 노출되는 회귀를 막기 위해 onClick만으로는 전환되지 않는다).
	 */
	ariaLabel?: string
}

export const TMediaCard = React.memo((props: TMediaCardProps) => {
	const {
		cardRef,
		cardHeader,
		cardFooter,
		cardOverlayContents,
		className,
		cardSelected,
		mediaSelected,
		visibleHoverRing = false,
		visibleHoverDim = false,
		draggable = false,
		isMenuOpen = false,
		dataAttributes,
		children,
		onClick,
		ariaLabel,
		...restProps
	} = props

	const [isHover, setHover] = useState<boolean>(false)
	const dataProps = useDataAttributes(dataAttributes)

	useEffect(() => {
		if (isMenuOpen) {
			setHover(false)
		}
	}, [isMenuOpen])

	const handleMouseEnter = React.useCallback(() => setHover(true), [])
	const handleMouseLeave = React.useCallback(() => setHover(false), [])

	const isInteractive = !!onClick && !!ariaLabel
	const handleKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLElement>) => {
			if (!isInteractive) return
			if (e.key !== "Enter" && e.key !== " ") return
			e.preventDefault()
			onClick?.()
		},
		[isInteractive, onClick]
	)

	return (
		<article ref={cardRef} draggable={draggable} className={clsx("TMediaCard", className)} {...restProps}>
			{cardHeader && <header>{cardHeader}</header>}
			<section className={clsx({ "card-selected": cardSelected })}>
				<main
					{...dataProps}
					role={isInteractive ? "button" : undefined}
					tabIndex={isInteractive ? 0 : undefined}
					aria-label={isInteractive ? ariaLabel : undefined}
					className={clsx({ "media-selected": mediaSelected, "hover-ring": visibleHoverRing, "cursor-pointer": onClick })}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onClick={onClick}
					onKeyDown={isInteractive ? handleKeyDown : undefined}
				>
					{cardOverlayContents && cardOverlayContents}
					{visibleHoverDim && <div className="hover-dim" style={{ opacity: isHover ? 1 : 0 }} />}
					{typeof children === "function" ? (children as (hover: boolean) => React.ReactNode)(isHover) : children}
				</main>
				{cardFooter && <footer>{cardFooter}</footer>}
			</section>
		</article>
	)
})

TMediaCard.displayName = "TMediaCard"
